/**
 * Lazy Load Images & Videos
 * Single IntersectionObserver (rootMargin 100px) for:
 * - Images: data-src/data-srcset; mega menu and card galleries handled separately
 * - Videos: deferred-media[autoplay] and video[autoplay], load/play when in view (first hero skipped)
 */

/**
 * Load a single lazy image
 * @param {HTMLImageElement} img
 */
function loadLazyImage(img) {
  // Skip if already loaded
  if (img.classList.contains("lazy-observed")) return;

  // Swap data-srcset to srcset
  if (img.dataset.srcset) {
    img.srcset = img.dataset.srcset;
    delete img.dataset.srcset;
  }

  // Swap data-src to src
  if (img.dataset.src) {
    img.src = img.dataset.src;
    delete img.dataset.src;
  }

  // Mark as observed
  img.classList.add("lazy-observed");
}

/**
 * Load all lazy images in a container
 * @param {HTMLElement} container
 */
function loadLazyImagesInContainer(container) {
  const lazyImages = container.querySelectorAll(
    "img[data-srcset], img[data-src]",
  );
  lazyImages.forEach((img) => loadLazyImage(/** @type {HTMLImageElement} */ (img)));
}

/**
 * Lazy image selector (excludes mega menu, which loads on hover)
 */
const LAZY_IMAGE_SELECTOR =
  "img[data-srcset]:not(.mega-menu__content img), img[data-src]:not(.mega-menu__content img)";

/** Breakpoint matching base.css: hidden--mobile applies at max-width 749px */
const MOBILE_MEDIA = typeof window !== "undefined" ? window.matchMedia("(max-width: 749px)") : null;

/**
 * True when viewport is mobile. On server/initial run, defaults to false.
 */
function isMobileViewport() {
  return MOBILE_MEDIA ? MOBILE_MEDIA.matches : false;
}

/**
 * Containers that are hidden on mobile (desktop-only). We should not load their images on mobile.
 */
const DESKTOP_ONLY_SELECTOR = ".hidden--mobile, .mobile\\:hidden";

/**
 * Returns true if the element is inside a desktop-only container and we're on mobile.
 * @param {Element} element
 */
function isInDesktopOnlyOnMobile(element) {
  if (!isMobileViewport()) return false;
  return element.closest(DESKTOP_ONLY_SELECTOR) !== null;
}

/**
 * Returns true if this image should be observed (not already loaded).
 * We still observe desktop-only images so they load if the user resizes to desktop.
 * @param {HTMLImageElement} img
 */
function shouldObserveLazyImage(img) {
  return !img.classList.contains("lazy-observed");
}

/**
 * For product card galleries: only the first image (by DOM order) should load when the card is in view.
 * Second and later images load on hover (or when slideshow advances).
 * @param {HTMLImageElement} img
 */
function isFirstImageInCardGallery(img) {
  const gallery = img.closest(".card-gallery");
  if (!gallery) return true; // Not in a card gallery, allow load
  const firstImg = gallery.querySelector("img");
  return firstImg === img;
}

/**
 * True if the element is in or near the viewport (e.g. within margin of top/bottom).
 * Used so we only prioritise images for card galleries that are actually on screen.
 * @param {Element} element
 * @param {number} marginPx
 */
function isInOrNearViewport(element, marginPx) {
  const rect = element.getBoundingClientRect();
  const top = rect.top - marginPx;
  const bottom = rect.bottom + marginPx;
  return top < window.innerHeight && bottom > 0;
}

// Single IntersectionObserver for lazy images and autoplay videos (one rootMargin for all)
const LAZY_ROOT_MARGIN = "100px";
const lazyObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;

      if (el instanceof HTMLImageElement) {
        if (isInDesktopOnlyOnMobile(el)) return;
        if (!isFirstImageInCardGallery(el)) return;
        loadLazyImage(el);
      } else if (el instanceof HTMLVideoElement) {
        const playPromise = el.play();
        if (playPromise !== undefined) playPromise.catch(() => {});
      } else if (el.matches?.("deferred-media")) {
        const playButton = el.querySelector('[ref="deferredMediaPlayButton"]');
        if (playButton instanceof HTMLElement) {
          playButton.click();
        } else {
          const custom = /** @type {{ showDeferredMedia?: () => void }} */ (el);
          if (typeof custom.showDeferredMedia === "function") custom.showDeferredMedia();
        }
      }

      lazyObserver.unobserve(el);
    });
  },
  { rootMargin: LAZY_ROOT_MARGIN }
);

/**
 * Observe lazy images within a container (adds them to the IntersectionObserver).
 * Also loads any that are in/near viewport after layout (e.g. product recommendations):
 * viewport check is deferred so getBoundingClientRect() is correct for newly injected content.
 * @param {ParentNode} container
 */
function observeLazyImagesInContainer(container) {
  const lazyImages = container.querySelectorAll(LAZY_IMAGE_SELECTOR);
  const viewportMargin = 50;
  lazyImages.forEach((img) => {
    const imageEl = /** @type {HTMLImageElement} */ (img);
    if (!shouldObserveLazyImage(imageEl)) return;
    lazyObserver.observe(img);
  });
  // After layout, load first images in card galleries that are already in view
  // (newly injected content often has wrong getBoundingClientRect() synchronously)
  requestAnimationFrame(() => {
    lazyImages.forEach((img) => {
      const imageEl = /** @type {HTMLImageElement} */ (img);
      if (!shouldObserveLazyImage(imageEl)) return;
      if (!isInOrNearViewport(img, viewportMargin) || isInDesktopOnlyOnMobile(imageEl) || !isFirstImageInCardGallery(imageEl)) return;
      loadLazyImage(imageEl);
      lazyObserver.unobserve(img);
    });
  });
}

/**
 * Initialize lazy loading for all images and prioritise in-view card galleries.
 */
function initializeLazyLoading() {
  const lazyImages = document.querySelectorAll(LAZY_IMAGE_SELECTOR);
  lazyImages.forEach((img) => lazyObserver.observe(img));

  const cardGalleries = document.querySelectorAll(".card-gallery");
  const viewportMargin = 20;
  cardGalleries.forEach((gallery) => {
    if (!isInOrNearViewport(gallery, viewportMargin)) return;
    if (isInDesktopOnlyOnMobile(gallery)) return;
    const firstLazyImage = gallery.querySelector("img[data-srcset], img[data-src]");
    if (firstLazyImage) {
      loadLazyImage(/** @type {HTMLImageElement} */ (firstLazyImage));
      lazyObserver.unobserve(firstLazyImage);
    }
  });
}

/**
 * Initialize lazy loading for autoplay videos (deferred-media and direct video).
 * First hero section videos are skipped and play immediately.
 */
function initializeVideoLazyLoading() {
  const firstHero = document.querySelector(".hero-wrapper:first-of-type .hero");
  const firstHeroSection = firstHero?.closest(".shopify-section");
  const firstHeroSectionId =
    firstHeroSection instanceof HTMLElement ? firstHeroSection.dataset.section : null;

  document.querySelectorAll("deferred-media[autoplay]").forEach((el) => {
    const parentSection = el.closest(".shopify-section");
    const isInFirstHero =
      parentSection instanceof HTMLElement &&
      firstHeroSectionId &&
      parentSection.dataset.section === firstHeroSectionId;
    if (isInFirstHero) return;
    el.removeAttribute("autoplay");
    lazyObserver.observe(el);
  });

  document.querySelectorAll("video[autoplay]").forEach((video) => {
    const parentSection = video.closest(".shopify-section");
    const isInFirstHero =
      parentSection instanceof HTMLElement &&
      firstHeroSectionId &&
      parentSection.dataset.section === firstHeroSectionId;
    if (isInFirstHero) return;
    video.removeAttribute("autoplay");
    lazyObserver.observe(video);
  });
}

/**
 * Set up MutationObserver to observe dynamically added content (e.g. product recommendations
 * section that injects HTML when it enters the viewport).
 */
function setupMutationObserver() {
  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== "childList" || !mutation.addedNodes.length) continue;
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) {
          observeLazyImagesInContainer(node);
        } else if (node instanceof DocumentFragment) {
          observeLazyImagesInContainer(node);
        }
      });
    }
  });

  if (document.body) {
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }
}

// Initialize on DOMContentLoaded
function init() {
  initializeLazyLoading();
  initializeVideoLazyLoading();
  setupMutationObserver();
}
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

// Load images when menu opens (mega menu specific)
document.addEventListener("megaMenu:hover", () => {
  const menuResourceCards = document.querySelectorAll(".mega-menu__content .resource-card");
  
  menuResourceCards.forEach((cardElement) => {
    const card = /** @type {HTMLElement} */ (cardElement);
    loadLazyImagesInContainer(card);
  });
});

// Product card galleries: load second (and rest) images when user hovers so hover-to-reveal works
document.addEventListener("pointerenter", (e) => {
  const target = e.target;
  if (!(target instanceof Element)) return;
  const gallery = target.closest(".card-gallery");
  if (!gallery) return;
  loadLazyImagesInContainer(/** @type {HTMLElement} */ (gallery));
}, true);
